"use client";

import { useState, useCallback, useEffect } from "react";
import {
  BoosterConfigSummary,
  BoosterSheetSummary,
} from "modules/cards/model/set";
import {
  RosenPieChart,
  PieChartDataItem,
  TooltipContent,
} from "modules/common/components/charts/RosenPieChart";
import { Tabs, TabsList, TabsTrigger } from "modules/common/components/ui/tabs";
import { ChevronLeft } from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import {
  formatBoosterName,
  formatSheetName,
} from "../../utils/boosterBreakdown";

interface BoosterChartPanelProps {
  boosterConfigs: Record<string, BoosterConfigSummary>;
}

const CHART_COLORS = [
  "text-primary",
  "text-secondary",
  "text-accent",
  "text-success",
  "text-warning",
  "text-error",
  "text-info",
  "text-neutral",
  "text-primary/70",
  "text-secondary/70",
];

export function BoosterChartPanel({ boosterConfigs }: BoosterChartPanelProps) {
  const entries = Object.entries(boosterConfigs);
  const hasMultipleConfigs = entries.length > 1;

  const [mounted, setMounted] = useState(false);
  const [selectedConfigKey, setSelectedConfigKey] = useState(entries[0][0]);
  const [drillDownVariationIndex, setDrillDownVariationIndex] = useState<
    number | null
  >(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const config = boosterConfigs[selectedConfigKey];
  const hasMultipleVariations = config.variations.length > 1;

  const animateTransition = useCallback((callback: () => void) => {
    setIsTransitioning(true);
    setTimeout(() => {
      callback();
      setIsTransitioning(false);
    }, 150);
  }, []);

  const handleSliceClick = useCallback(
    (_item: PieChartDataItem, index: number) => {
      if (drillDownVariationIndex !== null) return;
      animateTransition(() => setDrillDownVariationIndex(index));
    },
    [drillDownVariationIndex, animateTransition]
  );

  const handleBack = useCallback(() => {
    animateTransition(() => setDrillDownVariationIndex(null));
  }, [animateTransition]);

  const handleConfigChange = useCallback((value: string | number) => {
    setSelectedConfigKey(String(value));
    setDrillDownVariationIndex(null);
  }, []);

  // Build chart data based on current view
  const isSlotView = drillDownVariationIndex !== null;

  const chartData: PieChartDataItem[] = isSlotView
    ? config.variations[drillDownVariationIndex].slots.map((slot, i) => ({
        name: formatSheetName(slot.sheetName),
        value: slot.count,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }))
    : config.variations.map((v, i) => ({
        name: `Var ${i + 1}`,
        value: v.probability,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }));

  const tooltipFormatter = useCallback(
    (item: PieChartDataItem): TooltipContent => {
      if (isSlotView) {
        const variation = config.variations[drillDownVariationIndex];
        const slotIndex = variation.slots.findIndex(
          (s) => formatSheetName(s.sheetName) === item.name
        );
        const slot = variation.slots[slotIndex];
        const sheet: BoosterSheetSummary | undefined = slot
          ? config.sheets[slot.sheetName]
          : undefined;

        const items: { label: string; value?: string | number }[] = [
          { label: "Cards drawn", value: item.value },
        ];
        if (sheet) {
          items.push({ label: "Pool size", value: sheet.cardCount });
          if (sheet.foil) items.push({ label: "Foil", value: "Yes" });
          if (sheet.fixed) items.push({ label: "Fixed", value: "Yes" });
        }

        return { title: item.name, items };
      }

      // Variations view
      const varIndex = config.variations.findIndex(
        (_, i) => `Var ${i + 1}` === item.name
      );
      const variation = config.variations[varIndex];
      if (!variation) return { title: item.name };

      return {
        title: `Variation ${varIndex + 1} (${variation.probability.toFixed(1)}%)`,
        items: variation.slots.map((slot) => ({
          label: formatSheetName(slot.sheetName),
          value: `×${slot.count}`,
        })),
      };
    },
    [isSlotView, config, drillDownVariationIndex]
  );

  const configName = formatBoosterName(config.name || selectedConfigKey);

  // Center label content
  const centerLabel = isSlotView ? (
    <button
      onClick={handleBack}
      className="text-base-content/60 hover:text-base-content flex cursor-pointer items-center gap-0.5 text-xs font-medium transition-colors"
    >
      <ChevronLeft size={ICON_SIZE} />
      Back
    </button>
  ) : (
    <span className="text-base-content/40 text-center text-[9px] leading-tight">
      Click slice
      <br />
      to drill down
    </span>
  );

  // Single variation: show summary instead of chart
  if (!hasMultipleVariations) {
    const totalSlots = config.variations[0]?.slots.reduce(
      (sum, s) => sum + s.count,
      0
    );

    return (
      <div className="flex flex-col gap-2">
        <h3 className="text-base-content text-lg font-semibold">
          {configName}
        </h3>
        <div className="card bg-base-100 border-base-content/20 h-fit shrink-0 grow-0 border p-4 shadow-sm">
          {hasMultipleConfigs && (
            <ConfigTabs
              entries={entries}
              selectedKey={selectedConfigKey}
              onChange={handleConfigChange}
            />
          )}

          <p className="text-base-content/60 text-sm">
            Single configuration &mdash; {totalSlots} card
            {totalSlots !== 1 ? "s" : ""} per pack
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-base-content text-lg font-semibold">{configName}</h3>

      <div className="card bg-base-100 border-base-content/20 h-fit shrink-0 grow-0 border p-4 shadow-sm">
        {hasMultipleConfigs && (
          <ConfigTabs
            entries={entries}
            selectedKey={selectedConfigKey}
            onChange={handleConfigChange}
          />
        )}

        <p className="text-base-content/50 mb-2 text-xs">
          {isSlotView
            ? `Variation ${drillDownVariationIndex + 1} — Slot breakdown`
            : `${config.variations.length} variations`}
        </p>
        {mounted ? (
          <div
            className={`transition-all duration-150 ${
              isTransitioning ? "scale-95 opacity-0" : "scale-100 opacity-100"
            }`}
          >
            <RosenPieChart
              data={chartData}
              tooltipFormatter={tooltipFormatter}
              onSliceClick={isSlotView ? undefined : handleSliceClick}
              centerLabel={centerLabel}
              height={250}
              width={250}
              innerRadius={45}
              outerRadius={100}
              showValues={false}
              colors={CHART_COLORS}
            />
          </div>
        ) : (
          <div
            className="skeleton flex items-center justify-center rounded-full"
            style={{ height: 250, width: 250 }}
          />
        )}
      </div>
    </div>
  );
}

// --- Sub-components & helpers ---

interface ConfigTabsProps {
  entries: [string, BoosterConfigSummary][];
  selectedKey: string;
  onChange: (value: string | number) => void;
}

function ConfigTabs({ entries, selectedKey, onChange }: ConfigTabsProps) {
  return (
    <Tabs value={selectedKey} onValueChange={onChange} className="mb-3">
      <TabsList variant="boxed" className="w-full">
        {entries.map(([key, cfg]) => (
          <TabsTrigger key={key} value={key} className="text-xs">
            {formatBoosterName(cfg.name || key)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
